import axios from 'axios'
import { env } from '../config/environment'

// Get API URL from environment configuration
const API_BASE_URL = env.apiBaseUrl

export const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/api/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Export as 'api' for convenience
export const api = adminApi

// Request interceptor to add auth token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle auth errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored auth data on 401
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export interface AdminConfig {
  id: string
  category: string
  key: string
  value: any
  isEncrypted: boolean
  description?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface AdminUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  isActive: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt: string
}

export interface AuditLog {
  id: string
  adminUserId: string
  action: string
  resourceType?: string
  resourceId?: string
  oldValues?: any
  newValues?: any
  ipAddress?: string
  userAgent?: string
  createdAt: string
  adminUser: {
    firstName: string
    lastName: string
    email: string
  } | null
}

// Configuration API
export const configApi = {
  getByCategory: (category: string): Promise<AdminConfig[]> =>
    adminApi.get(`/config/${category}`).then(res => res.data),
  
  get: (category: string, key: string): Promise<{ value: any }> =>
    adminApi.get(`/config/${category}/${key}`).then(res => res.data),
  
  set: (category: string, key: string, data: { value: any; encrypt?: boolean; description?: string }): Promise<AdminConfig> =>
    adminApi.put(`/config/${category}/${key}`, data).then(res => res.data),
  
  delete: (category: string, key: string): Promise<void> =>
    adminApi.delete(`/config/${category}/${key}`).then(res => res.data),
}

// User API
export const userApi = {
  list: (): Promise<AdminUser[]> =>
    adminApi.get('/users').then(res => res.data),
  
  create: (userData: { email: string; password: string; firstName: string; lastName: string; role: string }): Promise<AdminUser> =>
    adminApi.post('/users', userData).then(res => res.data),
  
  update: (id: string, userData: Partial<AdminUser>): Promise<AdminUser> =>
    adminApi.put(`/users/${id}`, userData).then(res => res.data),
  
  delete: (id: string): Promise<void> =>
    adminApi.delete(`/users/${id}`).then(res => res.data),
}

// Audit Log API
export const auditApi = {
  list: (params?: { page?: number; limit?: number; action?: string; resourceType?: string }): Promise<{ logs: AuditLog[]; total: number; page: number; limit: number }> =>
    adminApi.get('/audit-logs', { params }).then(res => res.data),
  
  getByResource: (resourceType: string, resourceId: string): Promise<AuditLog[]> =>
    adminApi.get(`/audit-logs/resource/${resourceType}/${resourceId}`).then(res => res.data),
}

export interface MobileClientLogFileSummary {
  filename: string
  size: number
  modified: string
}

export interface PublicChatLogSummary {
  sessionId: string
  createdAt: string
  updatedAt: string
  messageCount: number
  visitorName: string | null
  visitorEmail: string | null
  preview: string | null
}

export interface PublicChatLogDetail {
  sessionId: string
  createdAt: string
  updatedAt: string
  visitor: {
    name?: string
    email?: string
    phone?: string
    company?: string
    businessType?: string
  }
  messages: { role: 'user' | 'assistant'; content: string; timestamp: string }[]
}

export const publicChatLogsApi = {
  list: (params?: { page?: number; limit?: number }): Promise<{
    success: boolean
    logs: PublicChatLogSummary[]
    total: number
    page: number
    limit: number
  }> => adminApi.get('/public-chat-logs', { params }).then(res => res.data),

  get: (sessionId: string): Promise<{ success: boolean; data: PublicChatLogDetail }> =>
    adminApi.get(`/public-chat-logs/${encodeURIComponent(sessionId)}`).then(res => res.data),
}

export const mobileClientLogsApi = {
  listFiles: (params?: { page?: number; limit?: number }): Promise<{
    success: boolean
    files: MobileClientLogFileSummary[]
    total: number
    page: number
    limit: number
  }> => adminApi.get('/mobile-client-logs/files', { params }).then(res => res.data),

  getFile: (filename: string): Promise<{ success: boolean; data: unknown }> =>
    adminApi.get(`/mobile-client-logs/files/${encodeURIComponent(filename)}`).then(res => res.data)
}

// Marketing API Types
export interface MarketingAccount {
  id: string
  type: string
  businessId: string | null
  isActive: boolean
  subscriptionTier: string | null
  emailQuota: number
  emailsSent: number
  quotaResetDate: string
  createdAt: string
}

export interface MarketingCampaign {
  id: string
  accountId: string
  name: string
  description?: string
  campaignType: 'drip' | 'one-time' | 'triggered'
  templateName?: string
  steps: any[]
  isActive: boolean
  enrollmentsCount: number
  activeEnrollmentsCount: number
  completedCount: number
  openRate?: number
  clickRate?: number
  createdById: string
  createdAt: string
  updatedAt: string
  _count?: {
    enrollments: number
    sentEmails: number
  }
}

export interface MarketingContact {
  id: string
  accountId: string
  sourceBusinessId?: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  companyName?: string
  subscriptionPlan?: string
  signupDate?: string
  unsubscribed: boolean
  unsubscribedAt?: string
  tags: string[]
  metadata: any
  createdAt: string
}

export interface CampaignAnalytics {
  campaignId: string
  campaignName: string
  enrollments: number
  emailsSent: number
  delivered: number
  opened: number
  clicked: number
  bounced: number
  failed: number
  openRate: number
  clickRate: number
  bounceRate: number
}

// Marketing API
const marketingApi = axios.create({
  baseURL: `${API_BASE_URL}/api/marketing`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth interceptor to marketing API
marketingApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const marketingAPIService = {
  // Account
  getAccount: (): Promise<{ success: boolean; account: MarketingAccount }> =>
    marketingApi.get('/account').then(res => res.data),

  // Campaigns
  getCampaigns: (params?: { isActive?: boolean; campaignType?: string }): Promise<{ success: boolean; campaigns: MarketingCampaign[] }> =>
    marketingApi.get('/campaigns', { params }).then(res => res.data),
  
  getCampaignById: (campaignId: string): Promise<{ success: boolean; campaign: MarketingCampaign }> =>
    marketingApi.get(`/campaigns/${campaignId}`).then(res => res.data),
  
  createCampaign: (data: {
    name: string
    description?: string
    campaignType: 'drip' | 'one-time' | 'triggered' | 'nurturing' | 're-engagement' | 'win-back' | 'abandonment' | 'welcome'
    templateName?: string
    steps: any[]
    isActive?: boolean
  }): Promise<{ success: boolean; campaign: MarketingCampaign }> =>
    marketingApi.post('/campaigns', data).then(res => res.data),
  
  updateCampaign: (campaignId: string, data: Partial<MarketingCampaign>): Promise<{ success: boolean; campaign: MarketingCampaign }> =>
    marketingApi.put(`/campaigns/${campaignId}`, data).then(res => res.data),
  
  deleteCampaign: (campaignId: string): Promise<{ success: boolean; message: string }> =>
    marketingApi.delete(`/campaigns/${campaignId}`).then(res => res.data),

  // Enrollments
  enrollContacts: (campaignId: string, contactIds: string[]): Promise<{ success: boolean; enrolled: string[]; skipped: any[] }> =>
    marketingApi.post(`/campaigns/${campaignId}/enroll`, { contactIds }).then(res => res.data),
  
  getCampaignEnrollments: (campaignId: string, params?: { status?: string }): Promise<{ success: boolean; enrollments: any[] }> =>
    marketingApi.get(`/campaigns/${campaignId}/enrollments`, { params }).then(res => res.data),

  // Analytics
  getCampaignAnalytics: (campaignId: string): Promise<{ success: boolean; analytics: CampaignAnalytics }> =>
    marketingApi.get(`/campaigns/${campaignId}/analytics`).then(res => res.data),

  // Contacts
  getContacts: (params?: {
    unsubscribed?: boolean
    tags?: string
    search?: string
    page?: number
    limit?: number
  }): Promise<{
    success: boolean
    contacts: MarketingContact[]
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
    }
  }> =>
    marketingApi.get('/contacts', { params }).then(res => res.data),
  
  createContact: (data: {
    email: string
    firstName: string
    lastName: string
    phone?: string
    companyName?: string
    tags?: string[]
    metadata?: any
  }): Promise<{ success: boolean; contact: MarketingContact }> =>
    marketingApi.post('/contacts', data).then(res => res.data),
  
  importFromBusinesses: (): Promise<{ success: boolean; imported: number; skipped: number; skippedDetails: any[] }> =>
    marketingApi.post('/contacts/import').then(res => res.data),
  
  deleteContact: (contactId: string): Promise<{ success: boolean; message: string }> =>
    marketingApi.delete(`/contacts/${contactId}`).then(res => res.data),
  
  unsubscribeContact: (contactId: string): Promise<{ success: boolean; message: string }> =>
    marketingApi.post(`/contacts/${contactId}/unsubscribe`).then(res => res.data),

  // Templates (legacy - code defaults)
  getTemplates: (): Promise<{ success: boolean; templates: string[] }> =>
    marketingApi.get('/templates').then(res => res.data),
  
  getTemplate: (templateName: string): Promise<{ success: boolean; template: any }> =>
    marketingApi.get(`/templates/${templateName}`).then(res => res.data),

  getDefaultTemplates: (): Promise<{ success: boolean; templates: any; templateNames: string[] }> =>
    marketingApi.get('/templates/defaults/list').then(res => res.data),

  // Business Template Management (businessId can be 'system' for ServiceFlow templates)
  getBusinessTemplates: (businessId: string | 'system', params?: { campaignType?: string; isActive?: boolean; templateName?: string }): Promise<{ success: boolean; templates: any[] }> =>
    marketingApi.get(`/templates/business/${businessId}`, { params }).then(res => res.data),

  getTemplateById: (templateId: string): Promise<{ success: boolean; template: any }> =>
    marketingApi.get(`/templates/id/${templateId}`).then(res => res.data),

  createTemplate: (businessId: string | 'system', data: {
    name: string
    description?: string
    templateName?: string
    campaignType: 'drip' | 'one-time' | 'triggered' | 'nurturing' | 're-engagement' | 'win-back' | 'abandonment' | 'welcome'
    steps: any[]
    isActive?: boolean
    isDefault?: boolean
    triggerEvent?: string
    triggerConditions?: any
    targetSegments?: any
  }): Promise<{ success: boolean; template: any }> =>
    marketingApi.post(`/templates/business/${businessId}`, data).then(res => res.data),

  updateTemplate: (templateId: string, data: {
    name?: string
    description?: string
    templateName?: string
    campaignType?: 'drip' | 'one-time' | 'triggered' | 'nurturing' | 're-engagement' | 'win-back' | 'abandonment' | 'welcome'
    steps?: any[]
    isActive?: boolean
    triggerEvent?: string
    triggerConditions?: any
    targetSegments?: any
  }): Promise<{ success: boolean; template: any }> =>
    marketingApi.put(`/templates/id/${templateId}`, data).then(res => res.data),

  deleteTemplate: (templateId: string): Promise<{ success: boolean; message: string }> =>
    marketingApi.delete(`/templates/id/${templateId}`).then(res => res.data),

  // Execution
  triggerExecution: (): Promise<{ success: boolean; message: string }> =>
    marketingApi.post('/execute').then(res => res.data),
}

// Lead Types
export interface Lead {
  id: string
  businessId: string | null
  firstName: string | null
  lastName: string | null
  email: string
  phone: string | null
  company: string | null
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  message: string | null
  sourceUrl: string | null
  sourceBlock: string | null
  status: string
  createdAt: string
  updatedAt: string
  assignedTo: string | null
  callId: string | null
  estimatedValue: number | null
  followUpDate: string | null
  notes: string | null
  priority: string
  source: string
  tags: any
  leadSource: string | null
  sms_marketing_consent: boolean
  sms_marketing_consent_date: string | null
  isServiceFlowLead: boolean
  convertedBusinessId: string | null
  conversionDate: string | null
  serviceArea: string | null
  websiteUrl: string | null
  googleStarRating: number | null
  googleReviewCount: number | null
  assignedUser?: {
    id: string
    firstName: string
    lastName: string
    email: string
  } | null
  convertedBusiness?: {
    id: string
    name: string
  } | null
  googlePlaceId?: string | null
  contactCount?: number
  contacts?: Array<{
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    title: string | null
    source: string
    createdAt: string
  }>
}

export interface CreateLeadData {
  firstName?: string
  lastName?: string
  email: string
  phone?: string
  company?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  message?: string
  sourceUrl?: string
  sourceBlock?: string
  status?: string
  priority?: string
  source?: string
  tags?: any
  leadSource?: string
  notes?: string
  estimatedValue?: number
  followUpDate?: string
  assignedTo?: string
  serviceArea?: string
  websiteUrl?: string
  googleStarRating?: number
  googleReviewCount?: number
}

export interface UpdateLeadData {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  company?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  message?: string
  sourceUrl?: string
  sourceBlock?: string
  status?: string
  priority?: string
  source?: string
  tags?: any
  leadSource?: string
  notes?: string
  estimatedValue?: number
  followUpDate?: string
  assignedTo?: string
  serviceArea?: string
  websiteUrl?: string
  googleStarRating?: number
  googleReviewCount?: number
}

export interface LeadStats {
  total: number
  byStatus: Record<string, number>
  byPriority: Record<string, number>
  conversionRate: number
  recentActivity: number
}

export interface NurturingTimelineItem {
  id: string
  taskType: string
  scheduledDate: string
  completedDate: string | null
  status: string
  notes: string | null
  metadata: any
  createdAt: string
  updatedAt: string
}

// Leads API
export const leadsApi = {
  list: (params?: {
    page?: number
    limit?: number
    search?: string
    status?: string
    priority?: string
    assignedTo?: string
    city?: string
    state?: string
    createdWithin?: 'today' | '7d' | '30d' | string
    fromMaps?: boolean
    emailQuality?: 'real' | 'placeholder' | string
    source?: string
    leadSource?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }): Promise<{
    success: boolean
    data: Lead[]
    pagination: {
      page: number
      limit: number
      total: number
    }
  }> =>
    adminApi.get('/leads', { params }).then(res => res.data),

  get: (id: string): Promise<{ success: boolean; data: Lead }> =>
    adminApi.get(`/leads/${id}`).then(res => res.data),

  create: (data: CreateLeadData): Promise<{ success: boolean; data: Lead }> =>
    adminApi.post('/leads', data).then(res => res.data),

  update: (id: string, data: UpdateLeadData): Promise<{ success: boolean; data: Lead }> =>
    adminApi.put(`/leads/${id}`, data).then(res => res.data),

  delete: (id: string): Promise<{ success: boolean; message: string }> =>
    adminApi.delete(`/leads/${id}`).then(res => res.data),

  assign: (id: string, userId?: string): Promise<{ success: boolean; data: Lead }> =>
    adminApi.post(`/leads/${id}/assign`, { userId }).then(res => res.data),

  convertToBusiness: (
    id: string,
    businessData: {
      name: string
      email?: string
      phone?: string
      subscriptionPlan?: string
      userLimit?: number
    }
  ): Promise<{
    success: boolean
    data: {
      lead: Lead
      business: any
    }
    message: string
  }> =>
    adminApi.post(`/leads/${id}/convert-to-business`, businessData).then(res => res.data),

  getStats: (): Promise<{ success: boolean; data: LeadStats }> =>
    adminApi.get('/leads/stats').then(res => res.data),

  getNurturingTimeline: (id: string): Promise<{ success: boolean; data: NurturingTimelineItem[] }> =>
    adminApi.get(`/leads/${id}/nurturing/timeline`).then(res => res.data),

  getSfDiscoveryPresets: (): Promise<{
    success: boolean
    data: { trades: string[]; geos: string[] }
  }> => adminApi.get('/sf-leads/presets').then(res => res.data),

  getSfWorkerStatus: (): Promise<{
    success: boolean
    data: {
      queue: {
        redisConfigured: boolean
        queueName: string
        counts: Record<string, number> | null
        error?: string
      }
      jobs: Array<{
        id: string
        type: string
        state: string
        attemptsMade: number
        timestamp: number
        processedOn: number | null
        finishedOn: number | null
        failedReason: string | null
        runId?: string
        data: Record<string, unknown>
      }>
      recentRuns: Array<{
        id: string
        status: string
        stats?: Record<string, number | string | null> | null
        error?: string | null
        createdAt: string
        updatedAt: string
        trades?: unknown
        geos?: unknown
        maxPerQuery?: number
      }>
      workerHint: string
      workerLikelyOnline: boolean
      deployTask: string
      docsPath: string
      maxPerQueryLimit?: number
    }
  }> => adminApi.get('/sf-leads/worker-status').then(res => res.data),

  listSfQueueJobs: (): Promise<{
    success: boolean
    data: Array<{
      id: string
      type: string
      state: string
      attemptsMade: number
      timestamp: number
      failedReason: string | null
      runId?: string
    }>
  }> => adminApi.get('/sf-leads/queue/jobs').then(res => res.data),

  cancelSfQueueJob: (jobId: string): Promise<{ success: boolean; data: { id: string; removed: boolean } }> =>
    adminApi.post(`/sf-leads/queue/jobs/${encodeURIComponent(jobId)}/cancel`).then(res => res.data),

  retrySfQueueJob: (jobId: string): Promise<{ success: boolean; data: { id: string; state: string } }> =>
    adminApi.post(`/sf-leads/queue/jobs/${encodeURIComponent(jobId)}/retry`).then(res => res.data),

  clearSfWaitingJobs: (): Promise<{ success: boolean; data: { removed: number } }> =>
    adminApi.delete('/sf-leads/queue/jobs/waiting').then(res => res.data),

  listSfDiscoveryRuns: (): Promise<{
    success: boolean
    data: Array<{
      id: string
      status: string
      stats?: Record<string, number> | null
      error?: string | null
      createdAt: string
      trades?: unknown
      geos?: unknown
    }>
  }> => adminApi.get('/sf-leads/discovery/runs').then(res => res.data),

  startSfDiscoveryRun: (body: {
    trades?: string[]
    geos?: string[]
    maxPerQuery?: number
  }): Promise<{
    success: boolean
    data: { runId: string; jobId?: string; status: string; queueStatus?: string }
  }> => adminApi.post('/sf-leads/discovery/runs', body).then(res => res.data),

  getSfDiscoveryRun: (id: string): Promise<{
    success: boolean
    data: {
      id: string
      status: string
      stats?: Record<string, number | string | null> | null
      error?: string | null
    }
  }> => adminApi.get(`/sf-leads/discovery/runs/${id}`).then(res => res.data),

  cancelSfDiscoveryRun: (id: string): Promise<{ success: boolean; data: any }> =>
    adminApi.post(`/sf-leads/discovery/runs/${id}/cancel`).then(res => res.data),

  startSfRefresh: (body?: { limit?: number }): Promise<{
    success: boolean
    data: { jobId?: string; queueStatus?: string; state?: string; limit?: number }
  }> => adminApi.post('/sf-leads/refresh', body || {}).then(res => res.data),

  startSfEnrich: (body?: { limit?: number }): Promise<{
    success: boolean
    data: { jobId?: string; queueStatus?: string; state?: string; limit?: number }
  }> => adminApi.post('/sf-leads/enrich', body || {}).then(res => res.data),

  exportSfToRangeLeads: (body: {
    workspaceId: string
    listName?: string
    listId?: string
    consentAttested: boolean
    city?: string
    state?: string
    status?: string
  }): Promise<{
    success: boolean
    data: { imported: number; skipped: number; listId: string }
  }> => adminApi.post('/sf-leads/export-rangeleads', body).then(res => res.data),
}

// Nurturing API service - uses admin API endpoints

export interface CreateNurturingTaskData {
  taskType: 'email' | 'call' | 'sms' | 'note'
  scheduledDate: string
  notes?: string
  metadata?: any
}

export interface UpdateNurturingTaskData {
  status?: 'pending' | 'completed' | 'skipped' | 'cancelled'
  scheduledDate?: string
  completedDate?: string
  notes?: string
  metadata?: any
}

export interface NurturingTask {
  id: string
  leadId: string
  businessId: string | null
  taskType: string
  scheduledDate: string
  completedDate: string | null
  status: string
  notes: string | null
  metadata: any
  createdAt: string
  updatedAt: string
}

export const nurturingApiService = {
  // Get nurturing tasks for a lead (using admin API)
  getTasks: (leadId: string): Promise<{ success: boolean; data: NurturingTask[] }> =>
    adminApi.get(`/leads/${leadId}/nurturing/tasks`).then(res => res.data),

  // Create nurturing task (using admin API)
  createTask: (
    leadId: string,
    data: CreateNurturingTaskData
  ): Promise<{ success: boolean; data: NurturingTask }> =>
    adminApi.post(`/leads/${leadId}/nurturing/tasks`, data).then(res => res.data),

  // Update nurturing task (using admin API)
  updateTask: (
    leadId: string,
    taskId: string,
    data: UpdateNurturingTaskData
  ): Promise<{ success: boolean; data: NurturingTask }> =>
    adminApi.put(`/leads/${leadId}/nurturing/tasks/${taskId}`, data).then(res => res.data),

  // Delete nurturing task (using admin API)
  deleteTask: (
    leadId: string,
    taskId: string
  ): Promise<{ success: boolean; message: string }> =>
    adminApi.delete(`/leads/${leadId}/nurturing/tasks/${taskId}`).then(res => res.data),
}

// Global Call Center Agents API
// Uses call-service-api endpoint
const CALL_SERVICE_URL = env.apiBaseUrl.replace('/api', '').replace(':3001', ':3005')

const callServiceApi = axios.create({
  baseURL: `${CALL_SERVICE_URL}/api/call-service`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth interceptor for call service API
callServiceApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export interface GlobalCallCenterAgent {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  skills: string[]
  priority: number
  isActive: boolean
  taskRouterSid?: string
  createdAt: string
  updatedAt: string
}

export interface CreateGlobalAgentData {
  firstName: string
  lastName: string
  email: string
  phone?: string
  skills?: string[]
  priority?: number
  isActive?: boolean
}

export interface UpdateGlobalAgentData {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  skills?: string[]
  priority?: number
  isActive?: boolean
  taskRouterSid?: string
}

export const globalAgentsApi = {
  list: (): Promise<GlobalCallCenterAgent[]> =>
    callServiceApi.get('/global-agents').then(res => res.data),

  get: (id: string): Promise<GlobalCallCenterAgent> =>
    callServiceApi.get(`/global-agents/${id}`).then(res => res.data),

  create: (data: CreateGlobalAgentData): Promise<GlobalCallCenterAgent> =>
    callServiceApi.post('/global-agents', data).then(res => res.data),

  update: (id: string, data: UpdateGlobalAgentData): Promise<GlobalCallCenterAgent> =>
    callServiceApi.put(`/global-agents/${id}`, data).then(res => res.data),

  delete: (id: string): Promise<{ success: boolean; message: string }> =>
    callServiceApi.delete(`/global-agents/${id}`).then(res => res.data),

  toggleActive: (id: string): Promise<GlobalCallCenterAgent> =>
    callServiceApi.patch(`/global-agents/${id}/toggle-active`).then(res => res.data),
}
